'use client';

/**
 * Care Coordination Hub Component
 *
 * Unified dashboard for TCO care coordination including:
 * - Dashboard overview with stats and quick actions
 * - Workflow instances with initiation capability
 * - Service referrals management
 * - Workflow templates management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  RefreshCw,
  Plus,
  Bot,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Users,
  Zap,
  BarChart3,
  Settings,
  ChevronRight,
  Loader2,
  Filter,
  Activity,
  FileText,
  Heart,
  UserCheck,
  Calendar,
  ArrowLeftRight,
  ClipboardList,
  LayoutDashboard,
  ShieldCheck,
} from 'lucide-react';
import {
  tcoWorkflowsAPI,
  WorkflowTemplate,
  WorkflowInstance,
  DashboardStats,
} from '@/services/api/tcoServiceReferrals';
import { useToast } from '@/components/ui/use-toast';
import { TCOServiceReferralsList } from './TCOServiceReferralsList';
import { WorkflowInitiationDialog } from './WorkflowInitiationDialog';
import { CareWorkflowReviewQueue } from './CareWorkflowReviewQueue';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface CareCoordinationHubProps {
  className?: string;
  onViewInstance?: (instanceId: number) => void;
  onViewReferral?: (referralId: number) => void;
  onEditTemplate?: (templateId: number) => void;
  onCreateTemplate?: () => void;
  onCreateReferral?: () => void;
}

type HubTab = 'dashboard' | 'review' | 'instances' | 'referrals' | 'templates';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
      return <Clock className="w-4 h-4 text-gray-500" />;
    case 'queued':
      return <Clock className="w-4 h-4 text-blue-500" />;
    case 'in_progress':
      return <Play className="w-4 h-4 text-blue-600" />;
    case 'waiting_response':
      return <Clock className="w-4 h-4 text-amber-500" />;
    case 'paused':
      return <Pause className="w-4 h-4 text-gray-500" />;
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'cancelled':
      return <XCircle className="w-4 h-4 text-gray-500" />;
    case 'failed':
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    default:
      return <Activity className="w-4 h-4 text-gray-400" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'bg-gray-100 text-gray-700';
    case 'queued':
      return 'bg-blue-100 text-blue-700';
    case 'in_progress':
      return 'bg-blue-100 text-blue-700';
    case 'waiting_response':
      return 'bg-amber-100 text-amber-700';
    case 'paused':
      return 'bg-gray-100 text-gray-600';
    case 'completed':
      return 'bg-green-100 text-green-700';
    case 'cancelled':
      return 'bg-gray-100 text-gray-600';
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const getTriggerIcon = (triggerType: string) => {
  switch (triggerType) {
    case 'sdoh_threshold':
      return <Heart className="w-4 h-4 text-red-500" />;
    case 'care_gap_detected':
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case 'enrollment_milestone':
      return <UserCheck className="w-4 h-4 text-green-500" />;
    case 'ai_recommended':
      return <Bot className="w-4 h-4 text-purple-500" />;
    case 'scheduled_review':
      return <Calendar className="w-4 h-4 text-blue-500" />;
    case 'manual':
      return <Users className="w-4 h-4 text-gray-500" />;
    default:
      return <Zap className="w-4 h-4 text-gray-400" />;
  }
};

// formatDate is now inside the component using useFormattedDate hook

// ============================================================================
// COMPONENT
// ============================================================================

export function CareCoordinationHub({
  className = '',
  onViewInstance,
  onViewReferral,
  onEditTemplate,
  onCreateTemplate,
  onCreateReferral,
}: CareCoordinationHubProps) {
  const { toast } = useToast();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  const formatDate = (dateStr: string) => {
    return fmt.dateTime(dateStr);
  };

  // State
  const [activeTab, setActiveTab] = useState<HubTab>('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [hasLoadError, setHasLoadError] = useState(false);
  const [showInitiateDialog, setShowInitiateDialog] = useState(false);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setHasLoadError(false);
    try {
      const [statsData, instancesData, templatesData] = await Promise.all([
        tcoWorkflowsAPI.getDashboard(),
        tcoWorkflowsAPI.listInstances({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        tcoWorkflowsAPI.listTemplates({ is_active: true }),
      ]);

      setStats(statsData);
      setInstances(instancesData);
      setTemplates(templatesData);
    } catch (err) {
      console.error('Failed to load care coordination data:', err);
      setHasLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Initial load
  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Manual refresh
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const [statsData, instancesData, templatesData] = await Promise.all([
        tcoWorkflowsAPI.getDashboard(),
        tcoWorkflowsAPI.listInstances({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        tcoWorkflowsAPI.listTemplates({ is_active: true }),
      ]);

      setStats(statsData);
      setInstances(instancesData);
      setTemplates(templatesData);
      toast({
        title: t('tcoReferrals.hub.refreshed', 'Refreshed'),
        description: t('tcoReferrals.hub.dataUpdated', 'Dashboard data updated'),
      });
    } catch (err) {
      console.error('Failed to refresh:', err);
      toast({
        title: t('tcoReferrals.hub.error', 'Error'),
        description: t('tcoReferrals.hub.failedToRefresh', 'Failed to refresh data'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter instances
  const filteredInstances = instances.filter((instance) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      instance.member_name?.toLowerCase().includes(query) ||
      instance.template_name?.toLowerCase().includes(query)
    );
  });

  // Filter templates
  const filteredTemplates = templates.filter((template) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.template_name?.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query)
    );
  });

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Toolbar (page title rendered by the shell chrome) */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('tcoReferrals.hub.refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as HubTab)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <LayoutDashboard className="w-4 h-4" />
            <span className="hidden sm:inline">{t('tcoReferrals.hub.tabs.dashboard', 'Dashboard')}</span>
          </TabsTrigger>
          <TabsTrigger value="review" className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            <span className="hidden sm:inline">{t('tcoReferrals.hub.tabs.review', 'Review queue')}</span>
          </TabsTrigger>
          <TabsTrigger value="instances" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            <span className="hidden sm:inline">{t('tcoReferrals.hub.tabs.workflows', 'Workflows')}</span>
          </TabsTrigger>
          <TabsTrigger value="referrals" className="flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4" />
            <span className="hidden sm:inline">{t('tcoReferrals.hub.tabs.referrals', 'Referrals')}</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            <span className="hidden sm:inline">{t('tcoReferrals.hub.tabs.templates', 'Templates')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Review queue Tab — connected-agent clinician gates */}
        <TabsContent value="review" className="space-y-4">
          <CareWorkflowReviewQueue />
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{t('tcoReferrals.hub.stats.inProgress', 'In Progress')}</p>
                    <p className="text-2xl font-bold">{stats?.total_in_progress ?? 0}</p>
                  </div>
                  <Play className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{t('tcoReferrals.hub.stats.awaitingResponse', 'Awaiting Response')}</p>
                    <p className="text-2xl font-bold">{stats?.total_waiting_response ?? 0}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{t('tcoReferrals.hub.stats.completed30d', 'Completed (30d)')}</p>
                    <p className="text-2xl font-bold">{stats?.total_completed_30d ?? 0}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{t('tcoReferrals.hub.stats.referralsCreated', 'Referrals Created')}</p>
                    <p className="text-2xl font-bold">{stats?.total_referrals_created_30d ?? 0}</p>
                  </div>
                  <FileText className="w-8 h-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{t('tcoReferrals.hub.stats.successRate', 'Success Rate')}</p>
                    <p className="text-2xl font-bold">
                      {stats?.success_rate ? `${Math.round(stats.success_rate)}%` : 'N/A'}
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions + Recent Activity */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('tcoReferrals.hub.quickActions', 'Quick Actions')}</CardTitle>
                <CardDescription>{t('tcoReferrals.hub.quickActionsDesc', 'Common care coordination tasks')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setActiveTab('instances')}
                >
                  <Play className="w-4 h-4 mr-2 text-blue-600" />
                  {t('tcoReferrals.hub.actions.startNewWorkflow', 'Start New Workflow')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={onCreateReferral}
                >
                  <Plus className="w-4 h-4 mr-2 text-green-600" />
                  {t('tcoReferrals.hub.actions.createServiceReferral', 'Create Service Referral')}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={onCreateTemplate}
                >
                  <ClipboardList className="w-4 h-4 mr-2 text-purple-600" />
                  {t('tcoReferrals.hub.actions.createWorkflowTemplate', 'Create Workflow Template')}
                </Button>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('tcoReferrals.hub.recentWorkflowActivity', 'Recent Workflow Activity')}</CardTitle>
                <CardDescription>{t('tcoReferrals.hub.latestUpdates', 'Latest workflow updates')}</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : instances.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">{t('tcoReferrals.hub.noRecentActivity', 'No recent workflow activity')}</p>
                ) : (
                  <div className="space-y-3">
                    {instances.slice(0, 5).map((instance) => (
                      <div
                        key={instance.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                        onClick={() => onViewInstance?.(instance.id)}
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(instance.status)}
                          <div>
                            <p className="font-medium text-sm">{instance.member_name}</p>
                            <p className="text-xs text-gray-500">{instance.template_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${getStatusColor(instance.status)}`}>
                            {instance.status.replace('_', ' ')}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Active Templates */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">{t('tcoReferrals.hub.activeWorkflowTemplates', 'Active Workflow Templates')}</CardTitle>
                <CardDescription>{t('tcoReferrals.hub.templatesAvailable', 'Templates available for use')}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={onCreateTemplate}>
                <Plus className="w-4 h-4 mr-1" />
                {t('tcoReferrals.hub.newTemplate', 'New Template')}
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : templates.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{t('tcoReferrals.hub.noActiveTemplates', 'No active templates')}</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  {templates.slice(0, 4).map((template) => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      onClick={() => onEditTemplate?.(template.id)}
                    >
                      <div className="flex items-center gap-3">
                        {getTriggerIcon(template.trigger_type)}
                        <div>
                          <p className="font-medium text-sm">{template.template_name}</p>
                          <p className="text-xs text-gray-500">{template.trigger_type.replace('_', ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {template.auto_trigger_enabled && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                            Auto
                          </Badge>
                        )}
                        {template.auto_create_referral && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                            Referral
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Instances Tab */}
        <TabsContent value="instances" className="space-y-4">
          {/* Header with Start Workflow Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t('tcoReferrals.hub.searchWorkflows', 'Search workflows...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-full sm:w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('tcoReferrals.hub.filters.allStatuses', 'All Statuses')}</SelectItem>
                  <SelectItem value="in_progress">{t('tcoReferrals.hub.filters.inProgress', 'In Progress')}</SelectItem>
                  <SelectItem value="waiting_response">{t('tcoReferrals.hub.filters.waitingResponse', 'Waiting Response')}</SelectItem>
                  <SelectItem value="paused">{t('tcoReferrals.hub.filters.paused', 'Paused')}</SelectItem>
                  <SelectItem value="completed">{t('tcoReferrals.hub.filters.completed', 'Completed')}</SelectItem>
                  <SelectItem value="failed">{t('tcoReferrals.hub.filters.failed', 'Failed')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="bg-v2accent hover:bg-v2accent/90" onClick={() => setShowInitiateDialog(true)}>
              <Play className="w-4 h-4 mr-2" />
              {t('tcoReferrals.hub.startWorkflow', 'Start Workflow')}
            </Button>
          </div>

          {/* Instances List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredInstances.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Activity className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">
                  {searchQuery ? t('tcoReferrals.hub.noWorkflowsMatch', 'No workflows match your search') : t('tcoReferrals.hub.noActiveWorkflows', 'No active workflows')}
                </p>
                <Button variant="outline" onClick={() => setShowInitiateDialog(true)}>
                  <Play className="w-4 h-4 mr-2" />
                  {t('tcoReferrals.hub.startFirstWorkflow', 'Start Your First Workflow')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredInstances.map((instance) => (
                <Card
                  key={instance.id}
                  className="cursor-pointer hover:border-blue-300 transition-colors"
                  onClick={() => onViewInstance?.(instance.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getStatusIcon(instance.status)}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{instance.member_name}</p>
                            {instance.service_referral_id && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                <FileText className="w-3 h-3 mr-1" />
                                {t('tcoReferrals.hub.referral', 'Referral')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{instance.template_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Progress */}
                        <div className="hidden sm:block text-right">
                          <p className="text-sm font-medium">
                            {t('tcoReferrals.hub.step', 'Step')} {instance.current_step}/{instance.total_steps}
                          </p>
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500"
                              style={{
                                width: `${(instance.current_step / instance.total_steps) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                        <Badge className={`${getStatusColor(instance.status)}`}>
                          {instance.status.replace('_', ' ')}
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Referrals Tab - Embed TCOServiceReferralsList */}
        <TabsContent value="referrals">
          <TCOServiceReferralsList
            onSelectReferral={onViewReferral}
            onCreateNew={onCreateReferral}
          />
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t('tcoReferrals.hub.searchTemplates', 'Search templates...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-64"
              />
            </div>
            <Button onClick={onCreateTemplate}>
              <Plus className="w-4 h-4 mr-2" />
              {t('tcoReferrals.hub.newTemplate', 'New Template')}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardList className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">
                  {searchQuery ? t('tcoReferrals.hub.noTemplatesMatch', 'No templates match your search') : t('tcoReferrals.hub.noWorkflowTemplates', 'No workflow templates')}
                </p>
                <Button variant="outline" onClick={onCreateTemplate}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('tcoReferrals.hub.createFirstTemplate', 'Create Your First Template')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredTemplates.map((template) => (
                <Card
                  key={template.id}
                  className="cursor-pointer hover:border-blue-300 transition-colors"
                  onClick={() => onEditTemplate?.(template.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getTriggerIcon(template.trigger_type)}
                        <div>
                          <p className="font-medium">{template.template_name}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">
                            {template.description || t('tcoReferrals.hub.noDescription', 'No description')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {template.trigger_type.replace('_', ' ')}
                        </Badge>
                        {template.auto_trigger_enabled && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                            {t('tcoReferrals.hub.autoTrigger', 'Auto-Trigger')}
                          </Badge>
                        )}
                        {template.auto_create_referral && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                            {t('tcoReferrals.hub.autoReferral', 'Auto-Referral')}
                          </Badge>
                        )}
                        <Button variant="ghost" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Workflow Initiation Dialog */}
      <WorkflowInitiationDialog
        open={showInitiateDialog}
        onClose={() => setShowInitiateDialog(false)}
        onSuccess={(workflowIds) => {
          setShowInitiateDialog(false);
          loadDashboardData(); // Refresh the list
          if (workflowIds.length === 1 && onViewInstance) {
            onViewInstance(workflowIds[0]); // Navigate to the new instance
          }
        }}
      />
    </div>
  );
}

export default CareCoordinationHub;
